import $ from '@/util/jQuery';

import { getGameType } from '@/util/Parsing';
import { getGoalsFromHTMLOutput } from '@/util/Parsing/Goals';
import { toCamelCase, phraseToUpper } from '@/util/String';

import {
  createShotEvent,
  createGoalEvent,
  createSaveEvent,
  createDeflectEvent,
  createMissedShotEvent,
  createShotHitPostEvent,
  createShotBlockedEvent,
  createShootoutAttemptEvent,
  createFaceoffEvent,
  createHitEvent,
  createPenaltyEvent,
  createMajorPenaltyEvent,
  createFightingEvent,
  createFightingPenaltyEvent,
  createMisconductPenaltyEvent,
  createEjectedEvent,
  createInjuryEvent,
  createOffsideEvent,
  createIcingEvent,
  createGoaliePulledEvent,
  createGoalieReturnedEvent,
  createGoalieReplacedEvent,
  createGoalReviewedEvent,
  createGoalReviewGoodEvent,
  createGoalReviewNoGoodEvent
} from '@/util/Parsing/Events/events';

/**
  Given a standard STHS HTML Output, will pull all the events of the game
  @params html - The STHS HTML Output
*/
export function getGameEventsFromHTMLOutput(html, teams){
  let eventHTML = [];

  // Select each of the possible period types
  $(html)
    .find("#tabmain4")
    .find("h3.STHSBoxScore_PlayByPlayPeriod, h3.STHSBoxScore_PlayByPlayOvertime, h3.STHSBoxScore_PlayByPlayShootout")
    .each((index, el) => {
      // Keep going until you hit another period
      $(el)
        .nextUntilWithTextNodes("h3.STHSBoxScore_PlayByPlayPeriod, h3.STHSBoxScore_PlayByPlayOvertime, h3.STHSBoxScore_PlayByPlayShootout")
        .each((i, el) => {
          let event;

          // If you hit a link tag.. end it all
          if(el.nodeName === "A"){
            return false;
          }

          // We don't care about <br/> or carriage return or single periods after bold text
          if(el.nodeName === 'BR' || el.data === '\n' || el.data === '. '){
            return;
          }

          // If it's a summary node, remove it as well
          if(el.nodeName !== 'B' && (el.data.includes("Goals for this period are") || el.data.includes("Shots for this period are") )){
            return;
          }

          // No data and a B node needs to be combined with the node before it
          if(!el.data && el.nodeName === 'B'){
            event = eventHTML.pop();
            event = `${event} ${el.innerText}`;
          } else{
              event = el.data;
          }

          // Push the event into the output
          eventHTML.push(event.trim());
        })
    })

    // Add more information into the goal scoring plays
    const goals = getGoalsFromHTMLOutput(html, teams);
    const events = convertEventsFromHTMLOutput(eventHTML);
    overwriteGoalEvents(events, goals);

    // Give event id to each event
    return createEventIds(events);
}

function overwriteGoalEvents(events, goals){
  goals.forEach(goal => {
    let { period, time } = goal;
    let timeEvents = events[period][time].events;

    for(let i = 0, length = timeEvents.length; i < length; i++){
      let result = timeEvents[i].result;

      // Find the goal event, morph it
      if(result && result.type === 'goal'){
          events[period][time].events[i].result = {
              ...result,
              team: goal.team,
              score: goal.score,
              player: goal.scorer,
              assists: goal.assists,
              variant: goal.variant
          }
          break;
      }
    }
  });
}

/**
  Given some HTML events from an STHS output, will convert them into data events
*/
function convertEventsFromHTMLOutput(events){
  const output = {};

  events.forEach(event => {
    let [time, desc] = event.split(" - ");

    // Determine period
    time = determineTimeAndPeriod(time);

    if(!output[time.key]){
      output[time.key] = {
        [time.time] : {
          time,
          events: [desc]
        }
      }
    } else if(!output[time.key][time.time]){
      output[time.key][time.time] = {
        time,
        events: [desc]
      };
    } else{
      output[time.key][time.time].events.push(desc);
    }
  })

  // Combine events into one object
  Object.keys(output).forEach(periodKey => {
    Object.keys(output[periodKey]).forEach(timeKey => {
      let events;

      if(periodKey === 'shootout') {
        events = mapShootout(output[periodKey][timeKey].events);
      } else {
        // TODO.. pass in team / goal info.. strip away fightPenalty.. useless
        events = output[periodKey][timeKey].events.map(event => mapEvent(event));
      }

      // Mutate the object, replacing the data
      output[periodKey][timeKey] = {
        time: output[periodKey][timeKey].time,
        events: combineSimilarEvents(events)
      };
    })
  })

  return output;
}


/**
 * Given a string of shootout events, will map accordingly
 * @param {string} event 
 */
function mapShootout(events){
  let output = [];
  if(events[0] === 'Round #1.'){
    const filteredEvents = events.filter(event => !event.includes('Round #'));

    // New style of shootout
    for(let i = 0, { length } = filteredEvents; i < length; i += 2){
        const [, player] = filteredEvents[i].split(', ');
        // This split is sometimes the last shot is on the same line as boilerplate text
        const [result] = filteredEvents[i + 1].split('\n');
        output.push(createShootoutAttemptEvent(player, result));
    }

  } else {
    // Backwards compatibility
    output = events.map(event => mapEvent(event));
  }

  return output;
}

function createEventIds(events){
  for(const period in events) {
    for(const time in events[period]){
      events[period][time].events.forEach((event, idx) => {
        events[period][time].events[idx] = {
          ...events[period][time].events[idx],
          id: `${period}-${time}-${idx}`,
        };
      })
    }
  }
  return events;
}


/**
 * Given a string event, will map to appropriate event
 * @param {string} event 
 */
function mapEvent(event){

  // Shot
  if(event.includes('Shot by ')){
    return createShotEvent(event);
  }

  // Goal
  if(event.includes('Goal by ')){
    return createGoalEvent(event);
  }

  // Save 
  if(event.includes('Stopped by ')){
    return createSaveEvent(event);
  }

  // Deflect
  if(event.includes('Deflect By ')){
    return createDeflectEvent(event);
  }

  // Shot Blocked
  if(event.includes('Shot Blocked by')){
    return createShotBlockedEvent(event);
  } 

  // Shot Missed
  if(event === 'Shot Misses the Net.'){
    return createMissedShotEvent()
  }

  // Shot Hit Post
  if(event === 'Shot Hit the Post.'){
    return createShotHitPostEvent()
  }

  // Faceoff
  if(event.includes('face-off')){
    return createFaceoffEvent(event);
  }

  // Hit
  if(event.includes('is hit by')){
    return createHitEvent(event);
  }

  // Minor Penalty
  if(event.includes('Minor Penalty')){
    return createPenaltyEvent(event);
  }

  // Major Penalty
  if(event.includes('Major Penalty')){
    return createMajorPenaltyEvent(event);
  }

  // Misconduct Penalty 
  if(event.includes('Misconduct Penalty')){
    return createMisconductPenaltyEvent(event);
  }

  // Figthting Penalty
  if(event.includes('Penalty')){
    return createFightingPenaltyEvent(event);
  }

  // Fight
  if(event.includes('fight') || event.includes('beats up')){
    return createFightingEvent(event);
  }


  // Ejected
  if(event.includes('ejected')){
    return createEjectedEvent(event);
  }

  // Injury
  if(event.includes('injured')){
    return createInjuryEvent(event);
  }

  // Offside
  if(event.includes('Off-side')){
    return createOffsideEvent(event);
  }

  // Icing
  if(event.includes('Icing')){
    return createIcingEvent(event);
  }
  
  // Goalie Pulled
  if(event.includes('pulled from the net')){
    return createGoaliePulledEvent(event);
  }

  // Goalie Returned
  if(event.includes('was sent back in the game')){
    return createGoalieReturnedEvent(event);
  }

  // Goalie Replaced
  if(event.includes(' enters game')){
    return createGoalieReplacedEvent(event);
  }

  // Goal Reviewed
  if(event === 'The goal is going for a video replay.'){
    return createGoalReviewedEvent();
  }

  // Goal Reviewed -- Good 
  if(event === 'The goal is good.'){
    return createGoalReviewGoodEvent();
  }

  if(event === 'The goal is refused.'){
    return createGoalReviewNoGoodEvent();
  }

  // WTF
  console.error(`NEW EVENT: ${event}`);
  debugger;
}


/*/////////////////////////////////////////
Takes a time from an events and converts into
a second timestamp for the period
/////////////////////////////////////////*/
function determineTimeAndPeriod(str) {
  let [time, period] = str.split(" of ");
  let [mins, secs] = time.split(":");

  time = (parseInt(mins) * 60) + parseInt(secs);
  let key = toCamelCase(period);

  // This is for regular season shootouts! They are displayed as 1st OT at 5:00
  if (getGameType() === 'Regular Season' && key === '1stOvertimePeriod' && time === 300){
    return {
      key: 'shootout',
      period: 'Shootout',
      time: 1
    }
  }

  return {
    key,
    period: phraseToUpper(period),
    time
  }
}


/**
 * Will combine similar event into one event
 * Ex: Deflected shot, goal, save
 * @param {array} events 
 */
function combineSimilarEvents(events){
  if(events.length === 1){
    return events;
  }

  let shotEvents = ['shot', 'penaltyShot'];
  let shotResultEvents = ['shotMissed', 'shotHitPost', 'shotBlocked', 'shotSaved', 'goal'];

  for(let i = 0; i < events.length; i++){
    let currentEvent = events[i].event;
    let nextEvent = events[i + 1] && events[i + 1].event;

    // Combine the deflect if the shot was deflected
    if(currentEvent === 'shot' && nextEvent === 'shotDeflect'){
      let { event, ...other } =  events[i + 1];
      events[i].deflect = { ...other }

      // Remove the deflect event
      events.splice((i + 1), 1);
      --i;
      
      continue;
    }

    // Goal Review
    if(shotEvents.includes(currentEvent) && nextEvent === 'goalReviewed'){
      events[i].review = { outcome: events[i + 2].event }

      // Remove the shot event
      events.splice((i + 1), 2);
      i = (i - 2) < 0 ? -1 : (i - 2);

      continue;
    }

    // Combine the shot events
    if(shotEvents.includes(currentEvent) && shotResultEvents.includes(nextEvent)){
      let { event, ...other } = events[i + 1];
      events[i].result = { type: event, ...other }

      // Remove the shot event
      events.splice((i + 1), 1);
      --i;

      continue;
    }
  }
  
  return events;
}

export default {
  getGameEventsFromHTMLOutput,
}